import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Send, Plus, Trash2, ChevronRight, ChevronLeft, CheckCircle, Sparkles, Loader2, Mic, Square, Eye, Download, Maximize2, ShoppingCart, CreditCard, ShieldCheck, Package, XCircle, Camera, Pencil } from 'lucide-react';
import { AnecdotePhotoUpload } from '../components/AnecdotePhotoUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_WORDS = 300;

const WordCounter = ({ text }) => {
  const words = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  const pct = Math.min(100, (words / MIN_WORDS) * 100);
  const isOk = words >= MIN_WORDS;
  return (
    <div className="mt-2" data-testid="word-counter">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={isOk ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
          {words} / {MIN_WORDS} palabras {isOk ? '— Perfecto' : ''}
        </span>
        <span className="text-muted-foreground">{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${isOk ? 'bg-green-500' : pct > 50 ? 'bg-amber-500' : 'bg-red-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const VoiceTextarea = ({ value, onChange, placeholder, testId, lang }) => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const accumulatedRef = useRef('');
  const supported = !!SpeechRecognition;

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(() => {
    if (!supported) {
      toast.error('Tu navegador no soporta el reconocimiento de voz. Usa Chrome o Edge.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = lang === 'en' ? 'en-US' : lang === 'fr' ? 'fr-FR' : lang === 'de' ? 'de-DE' : 'es-ES';
    recognition.continuous = true;
    recognition.interimResults = true;

    baseTextRef.current = value || '';
    accumulatedRef.current = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          accumulatedRef.current += transcript;
        } else {
          interim += transcript;
        }
      }
      const separator = baseTextRef.current && !baseTextRef.current.endsWith(' ') && !baseTextRef.current.endsWith('\n') ? ' ' : '';
      onChange(baseTextRef.current + separator + accumulatedRef.current + interim);
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error('Error en el reconocimiento de voz: ' + event.error);
      }
      stopRecording();
    };

    recognition.onend = () => {
      // Auto-restart if still recording (browser may stop after silence)
      if (recognitionRef.current) {
        try {
          baseTextRef.current = baseTextRef.current +
            (baseTextRef.current && !baseTextRef.current.endsWith(' ') ? ' ' : '') +
            accumulatedRef.current;
          accumulatedRef.current = '';
          recognition.start();
        } catch {
          stopRecording();
        }
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsRecording(true);
    } catch {
      toast.error('No se pudo iniciar el microfono');
    }
  }, [value, onChange, lang, supported, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <div>
      <div className="relative">
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || ''}
          className={`input-field min-h-[150px] pr-14 ${isRecording ? 'ring-2 ring-red-400 border-red-300' : ''}`}
          data-testid={testId}
        />
        {supported && (
          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            className={`absolute top-2 right-2 p-2 rounded-full transition-all ${
              isRecording
                ? 'bg-red-500 text-white shadow-lg shadow-red-200 animate-pulse'
                : 'bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary'
            }`}
            title={isRecording ? 'Detener grabacion' : 'Grabar con voz'}
            data-testid={`voice-btn-${testId}`}
          >
            {isRecording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
      </div>
      {isRecording && (
        <div className="flex items-center gap-2 mt-1.5 text-xs text-red-600" data-testid="voice-recording-indicator">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          Grabando... Habla con claridad. Pulsa el boton para detener.
        </div>
      )}
    </div>
  );
};

const DynamicField = ({ field, value, onChange, lang }) => {
  // Unique prefix so radio item ids never collide when the same field is
  // rendered multiple times (e.g. "certainty" inside repeated anecdotes).
  const uid = useId();
  switch (field.type) {
    case 'text':
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          className="input-field"
          data-testid={`field-${field.id}`}
        />
      );
    case 'textarea':
      return (
        <VoiceTextarea
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          testId={`field-${field.id}`}
          lang={lang}
        />
      );
    case 'select':
      return (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger className="input-field" data-testid={`field-${field.id}`}>
            <SelectValue placeholder="Selecciona..." />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'radio':
      return (
        <RadioGroup value={value || ''} onValueChange={onChange} data-testid={`field-${field.id}`}>
          {(field.options || []).map((opt) => (
            <div key={opt.value} className="flex items-center space-x-2">
              <RadioGroupItem value={opt.value} id={`${uid}_${field.id}_${opt.value}`} />
              <Label htmlFor={`${uid}_${field.id}_${opt.value}`} className="text-sm font-normal cursor-pointer">
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );
    case 'slider':
      return (
        <div className="space-y-2" data-testid={`field-${field.id}`}>
          <Slider
            value={[value || field.min_val || 1]}
            onValueChange={([v]) => onChange(v)}
            min={field.min_val || 1}
            max={field.max_val || 5}
            step={1}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{field.min_val || 1}</span>
            <span className="font-medium text-foreground">{value || field.min_val || 1}</span>
            <span>{field.max_val || 5}</span>
          </div>
        </div>
      );
    case 'photos':
      return null;
    case 'info':
      return (
        <p className="text-xs text-muted-foreground italic bg-amber-50/60 border border-amber-100 rounded-md px-3 py-2 leading-relaxed"
          data-testid={`info-${field.id}`}>
          {field.placeholder || ''}
        </p>
      );
    default:
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.id}`} />;
  }
};

const RepeatableSection = ({ section, instances, onUpdate, lang, errorKeys, clearError, inviteToken }) => {
  const addInstance = () => {
    if (instances.length < section.max_repeat) {
      onUpdate([...instances, {}]);
    }
  };

  const removeInstance = (idx) => {
    const updated = [...instances];
    updated.splice(idx, 1);
    onUpdate(updated);
  };

  const updateInstance = (idx, fieldId, value) => {
    const updated = [...instances];
    updated[idx] = { ...updated[idx], [fieldId]: value };
    onUpdate(updated);
    clearError?.(`${section.id}.${idx}.${fieldId}`);
  };

  return (
    <div className="space-y-4">
      {instances.map((instance, idx) => (
        <Card key={idx} className="border-l-4 border-l-primary/20">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">
                {section.title} {instances.length > 1 ? `#${idx + 1}` : ''}
              </span>
              {instances.length > 1 && (
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeInstance(idx)}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              )}
            </div>
            <div className="space-y-4">
              {section.fields.map((field) => {
                // Photos are rendered by AnecdotePhotoUpload below — skip here to avoid duplicate label.
                if (field.type === 'photos') return null;
                if (field.show_if && instance[field.show_if.field] !== field.show_if.equals) return null;
                if (field.type === 'info') {
                  return (
                    <DynamicField key={field.id} field={field} value={null} onChange={() => {}} lang={lang} />
                  );
                }
                const key = `${section.id}.${idx}.${field.id}`;
                const isMissing = errorKeys?.has(key);
                return (
                  <div key={field.id}
                    className={isMissing ? 'rounded-md ring-2 ring-red-400 bg-red-50/40 p-2 -mx-1' : ''}>
                    <Label className="input-label text-sm">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <DynamicField
                      field={field}
                      value={instance[field.id]}
                      onChange={(val) => updateInstance(idx, field.id, val)}
                      lang={lang}
                    />
                    {isMissing && (
                      <p className="text-[11px] text-red-600 mt-1">Este campo es obligatorio.</p>
                    )}
                  </div>
                );
              })}
              {(section.id === 'anecdote' || section.id === 'client_anecdote') && (
                <div>
                  <Label className="input-label text-sm">Fotos de la anecdota</Label>
                  <AnecdotePhotoUpload
                    photos={instance.photos || []}
                    onChange={(newPhotos) => updateInstance(idx, 'photos', newPhotos)}
                    bookId=""
                    context="contributor"
                    inviteToken={inviteToken}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
      {section.id === 'anecdote' && (
        <div className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid="ai-enrich-notice">
          <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-purple-700 leading-relaxed">
            No te preocupes por la redacción: al terminar, <strong>la IA nos ayudará a enriquecer la información que aportes</strong>, ampliándola con contexto de la época y de los lugares mencionados. Podrás revisar y ajustar el resultado antes de enviarlo.
          </p>
        </div>
      )}
      {instances.length < section.max_repeat && (
        <Button variant="outline" className="w-full gap-2 text-sm border-dashed" onClick={addInstance}>
          <Plus className="w-4 h-4" /> Anadir otra {section.title.toLowerCase()}
        </Button>
      )}
    </div>
  );
};

const ContributorThankYou = ({ info, token }) => {
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const [loadingPurchase, setLoadingPurchase] = useState(true);
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(() => new URLSearchParams(window.location.search).get('compra') === 'ok');
  const [purchaseDeclined, setPurchaseDeclined] = useState(false);
  const [purchaseTotal, setPurchaseTotal] = useState(null);
  const [confirmedName, setConfirmedName] = useState('');
  const [shipping, setShipping] = useState({ address: '', city: '', postal_code: '', phone: '' });
  const [billing, setBilling] = useState({ name: '', nif: '', email: '', address: '', city: '', postal_code: '' });

  // Referrals
  const [proposed, setProposed] = useState([]);
  const [showProposeForm, setShowProposeForm] = useState(false);
  const [proposeData, setProposeData] = useState({ name: '', last_name: '', email: '', relationship: '', note: '' });
  const [proposing, setProposing] = useState(false);

  const allowReferrals = !!info?.allow_referrals;

  useEffect(() => {
    const fetchPurchaseInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/contribute/${token}/purchase-info`);
        setPurchaseInfo(res.data);
        const fullName = [res.data.contributor_name, res.data.contributor_last_name].filter(Boolean).join(' ');
        setConfirmedName(fullName);
        setBilling(prev => ({ ...prev, name: fullName, email: res.data.contributor_email || '' }));
      } catch { /* silently fail */ }
      finally { setLoadingPurchase(false); }
    };
    fetchPurchaseInfo();
    if (allowReferrals) {
      axios.get(`${API_URL}/api/contribute/${token}/proposed`)
        .then(r => setProposed(r.data || []))
        .catch(() => {});
    }
  }, [token, allowReferrals]);

  const submitProposal = async () => {
    if (!proposeData.name.trim() || !proposeData.email.trim() || !proposeData.relationship) {
      toast.error('Por favor completa nombre, email y relación');
      return;
    }
    if (proposeData.note.trim().length < 10) {
      toast.error('Cuéntale al promotor por qué es importante (mínimo 10 caracteres)');
      return;
    }
    setProposing(true);
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/propose-collaborator`, proposeData);
      setProposed(prev => [...prev, {
        contributor_id: res.data.contributor_id,
        name: res.data.name,
        last_name: res.data.last_name,
        email: res.data.email,
        relationship: res.data.relationship,
        status: 'pending_approval',
        created_at: res.data.created_at,
      }]);
      setProposeData({ name: '', last_name: '', email: '', relationship: '', note: '' });
      setShowProposeForm(false);
      toast.success(`Hemos enviado tu propuesta. ${res.data.name} aparecerá en la lista del promotor para que la apruebe.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la propuesta');
    } finally {
      setProposing(false);
    }
  };

  const handlePurchase = async () => {
    if (!confirmedName.trim()) { toast.error('Confirma tu nombre completo'); return; }
    if (!shipping.address.trim() || !shipping.city.trim() || !shipping.postal_code.trim()) {
      toast.error('Completa la direccion de envio'); return;
    }
    setPurchasing(true);
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/purchase`, {
        confirmed_name: confirmedName,
        shipping_address: shipping,
        billing_profile: { ...billing, name: confirmedName },
        origin_url: window.location.origin,
      });
      if (res.data?.status === 'payment_required' && res.data?.checkout_url) {
        toast.info('Te llevamos a la pasarela de pago segura…');
        window.location.href = res.data.checkout_url;
        return;
      }
      setPurchaseComplete(true);
      setPurchaseTotal(res.data.total);
      toast.success('Compra realizada correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar la compra');
    } finally { setPurchasing(false); }
  };

  const copyPrice = purchaseInfo?.copy_price || 20;
  const shipPrice = purchaseInfo?.shipping_price || 5;
  const subtotal = copyPrice + shipPrice;
  const tax = Math.round(subtotal * (purchaseInfo?.tax_rate || 0.21) * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-md mx-auto space-y-6">
        {/* Thank You Card */}
        <Card>
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-heading font-bold">Gracias por tu aportacion</h2>
            <p className="text-muted-foreground">
              Tu contribucion al libro de <strong>{info?.recipient_name}</strong> ha sido registrada.
              Sera un regalo muy especial.
            </p>
          </CardContent>
        </Card>

        {/* Propose Collaborators (only if promoter enabled it) */}
        {allowReferrals && (
          <Card className="border-orange-200 bg-orange-50/40" data-testid="propose-collaborators-panel">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-orange-900">
                <Plus className="w-5 h-5 text-orange-600" />
                ¿Conoces a alguien más?
              </CardTitle>
              <CardDescription className="text-orange-800/80">
                Si crees que otra persona aportaría algo especial al libro de <strong>{info?.recipient_name}</strong>,
                propónsela al promotor. <strong>Tu propuesta no enviará nada automáticamente</strong> —
                el promotor decidirá si la invita o no.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Already-proposed list */}
              {proposed.length > 0 && (
                <div className="space-y-2" data-testid="my-proposals">
                  <p className="text-xs font-medium text-orange-900">Tus propuestas:</p>
                  {proposed.map((p) => (
                    <div key={p.contributor_id}
                      className="flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-md border border-orange-200">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{p.name}{p.last_name ? ` ${p.last_name}` : ''}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{p.email}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        p.status === 'pending_approval'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {p.status === 'pending_approval' ? 'Pendiente' : 'Aprobado'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!showProposeForm ? (
                <Button
                  className="w-full gap-2 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setShowProposeForm(true)}
                  data-testid="open-propose-form-btn"
                >
                  <Plus className="w-4 h-4" />
                  {proposed.length > 0 ? 'Proponer otra persona' : 'Proponer un colaborador'}
                </Button>
              ) : (
                <div className="space-y-3 bg-white p-4 rounded-md border border-orange-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Nombre <span className="text-red-500">*</span></Label>
                      <Input
                        value={proposeData.name}
                        onChange={(e) => setProposeData(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ej.: Lucia"
                        className="mt-1 input-field"
                        data-testid="propose-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Apellidos</Label>
                      <Input
                        value={proposeData.last_name}
                        onChange={(e) => setProposeData(p => ({ ...p, last_name: e.target.value }))}
                        placeholder="Ej.: Ruiz"
                        className="mt-1 input-field"
                        data-testid="propose-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                    <Input
                      type="email"
                      value={proposeData.email}
                      onChange={(e) => setProposeData(p => ({ ...p, email: e.target.value }))}
                      placeholder="email@ejemplo.com"
                      className="mt-1 input-field"
                      data-testid="propose-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Relación con {info?.recipient_name} <span className="text-red-500">*</span></Label>
                    <Select
                      value={proposeData.relationship}
                      onValueChange={(v) => setProposeData(p => ({ ...p, relationship: v }))}
                    >
                      <SelectTrigger className="mt-1 input-field" data-testid="propose-relationship">
                        <SelectValue placeholder="Selecciona…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="familia">Familia</SelectItem>
                        <SelectItem value="amigo">Amigo/a</SelectItem>
                        <SelectItem value="pareja">Pareja</SelectItem>
                        <SelectItem value="trabajo">Compañero/a de trabajo</SelectItem>
                        <SelectItem value="vecino">Vecino/a</SelectItem>
                        <SelectItem value="estudios">Compañero/a de estudios</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">
                      ¿Por qué crees que <strong>{info?.recipient_name || 'el protagonista'}</strong> querría leerle a esta persona en el libro? <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      value={proposeData.note}
                      onChange={(e) => setProposeData(p => ({ ...p, note: e.target.value }))}
                      placeholder={`Ej.: Es prima hermana de ${info?.recipient_name || 'el protagonista'}, han crecido juntas y comparten miles de anécdotas que harían mucha ilusión recoger en el libro.`}
                      className="mt-1 input-field min-h-[80px]"
                      data-testid="propose-note"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Esta justificación es lo que el promotor verá para decidir si la invita o no.
                    </p>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowProposeForm(false)}
                      data-testid="cancel-propose-btn"
                    >
                      Cancelar
                    </Button>
                    <Button
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white gap-2"
                      onClick={submitProposal}
                      disabled={proposing}
                      data-testid="submit-propose-btn"
                    >
                      {proposing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Enviar propuesta
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Purchase Panel */}
        {!loadingPurchase && purchaseInfo?.can_purchase && !purchaseComplete && !purchaseDeclined && (
          <Card className="border-primary/30 shadow-md" data-testid="contributor-purchase-panel">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShoppingCart className="w-5 h-5 text-primary" />
                ¿Quieres tu propio ejemplar?
              </CardTitle>
              <CardDescription>
                Puedes comprar tu copia del libro <strong>{purchaseInfo.book_title}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {!showPurchase ? (
                <div className="space-y-4">
                  {/* Price preview */}
                  <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Ejemplar del libro</span>
                      <span className="font-medium">{copyPrice.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Gastos de envio</span>
                      <span className="font-medium">{shipPrice.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>IVA (21%)</span>
                      <span>{tax.toFixed(2)}€</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-primary" data-testid="purchase-total">{total.toFixed(2)}€</span>
                    </div>
                  </div>
                  <Button
                    className="w-full gap-2"
                    onClick={() => setShowPurchase(true)}
                    data-testid="want-to-buy-btn"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Quiero mi ejemplar ({total.toFixed(2)}€)
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground hover:text-foreground"
                    onClick={() => setPurchaseDeclined(true)}
                    data-testid="decline-purchase-btn"
                  >
                    No, gracias — solo queria contribuir
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Confirmed Name */}
                  <div>
                    <Label className="text-xs font-medium">Tu nombre completo <span className="text-red-500">*</span></Label>
                    <Input
                      value={confirmedName}
                      onChange={(e) => setConfirmedName(e.target.value)}
                      placeholder="Nombre y apellidos"
                      className="mt-1"
                      data-testid="purchase-name"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Confirma que tu nombre es correcto</p>
                  </div>

                  {/* Shipping Address */}
                  <div className="space-y-3 pt-2 border-t">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Direccion de envio
                    </h4>
                    <div>
                      <Label className="text-xs text-muted-foreground">Direccion <span className="text-red-500">*</span></Label>
                      <Input
                        value={shipping.address}
                        onChange={(e) => setShipping(p => ({ ...p, address: e.target.value }))}
                        placeholder="Calle, numero, piso..."
                        className="mt-1"
                        data-testid="purchase-ship-address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Ciudad <span className="text-red-500">*</span></Label>
                        <Input
                          value={shipping.city}
                          onChange={(e) => setShipping(p => ({ ...p, city: e.target.value }))}
                          placeholder="Ciudad"
                          className="mt-1"
                          data-testid="purchase-ship-city"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Codigo postal <span className="text-red-500">*</span></Label>
                        <Input
                          value={shipping.postal_code}
                          onChange={(e) => setShipping(p => ({ ...p, postal_code: e.target.value }))}
                          placeholder="28010"
                          className="mt-1"
                          data-testid="purchase-ship-postal"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Telefono (opcional)</Label>
                      <Input
                        value={shipping.phone}
                        onChange={(e) => setShipping(p => ({ ...p, phone: e.target.value }))}
                        placeholder="600 000 000"
                        className="mt-1"
                        data-testid="purchase-ship-phone"
                      />
                    </div>
                  </div>

                  {/* Billing Data */}
                  <div className="space-y-3 pt-2 border-t">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" />
                      Datos de facturacion
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">NIF / CIF</Label>
                        <Input
                          value={billing.nif}
                          onChange={(e) => setBilling(p => ({ ...p, nif: e.target.value }))}
                          placeholder="12345678A"
                          className="mt-1"
                          data-testid="purchase-billing-nif"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Email</Label>
                        <Input
                          type="email"
                          value={billing.email}
                          onChange={(e) => setBilling(p => ({ ...p, email: e.target.value }))}
                          placeholder="email@ejemplo.com"
                          className="mt-1"
                          data-testid="purchase-billing-email"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Direccion fiscal</Label>
                      <Input
                        value={billing.address}
                        onChange={(e) => setBilling(p => ({ ...p, address: e.target.value }))}
                        placeholder="Misma que envio si no se indica"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Ciudad</Label>
                        <Input
                          value={billing.city}
                          onChange={(e) => setBilling(p => ({ ...p, city: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Codigo postal</Label>
                        <Input
                          value={billing.postal_code}
                          onChange={(e) => setBilling(p => ({ ...p, postal_code: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Price Summary */}
                  <div className="bg-muted/30 rounded-lg p-4 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span>Ejemplar</span><span>{copyPrice.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Envio</span><span>{shipPrice.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>IVA (21%)</span><span>{tax.toFixed(2)}€</span>
                    </div>
                    <div className="border-t pt-1.5 flex justify-between font-bold">
                      <span>Total</span><span className="text-primary">{total.toFixed(2)}€</span>
                    </div>
                  </div>

                  {/* Simulated payment notice */}
                  <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-[11px] text-amber-700">Pago simulado — Monei</span>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowPurchase(false)} className="flex-1">
                      Cancelar
                    </Button>
                    <Button
                      onClick={handlePurchase}
                      disabled={purchasing}
                      className="flex-1 gap-2"
                      data-testid="confirm-purchase-btn"
                    >
                      {purchasing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      {purchasing ? 'Procesando...' : `Comprar (${total.toFixed(2)}€)`}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Already purchased */}
        {purchaseInfo?.already_purchased && (
          <Card className="border-green-200 bg-green-50/30" data-testid="already-purchased">
            <CardContent className="py-5 text-center">
              <Package className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-700">Ya has comprado tu ejemplar</p>
              <p className="text-xs text-green-600 mt-1">Recibiras tu libro en 15-20 dias</p>
            </CardContent>
          </Card>
        )}

        {/* Purchase Complete */}
        {purchaseComplete && (
          <Card className="border-green-300 bg-green-50/50" data-testid="purchase-complete">
            <CardContent className="py-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Package className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-lg">¡Compra realizada!</h3>
              <p className="text-sm text-muted-foreground">
                Tu ejemplar de <strong>{purchaseInfo?.book_title}</strong> sera enviado a la direccion indicada.
              </p>
              <p className="text-xs text-muted-foreground">
                Total: <strong>{(purchaseTotal ?? total).toFixed(2)}€</strong> · Entrega estimada: 15-20 dias
              </p>
            </CardContent>
          </Card>
        )}

        {purchaseDeclined && !purchaseComplete && (
          <Card className="border-primary/20" data-testid="purchase-declined">
            <CardContent className="py-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Heart className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-lg">Sin compra · ¡Gracias por contribuir!</h3>
              <p className="text-sm text-muted-foreground">
                Tu aportacion al libro de <strong>{info?.recipient_name}</strong> ya esta guardada.
                Si cambias de opinion, vuelve a abrir el enlace de invitacion antes de la entrega del libro
                y podras solicitar tu ejemplar.
              </p>
              <Button
                variant="link"
                size="sm"
                className="text-xs"
                onClick={() => setPurchaseDeclined(false)}
                data-testid="reconsider-purchase-btn"
              >
                Mejor lo pienso un momento mas
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const ContributePage = () => {
  const { inviteToken: token } = useParams();
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errorKeys, setErrorKeys] = useState(new Set());
  const clearError = (key) => setErrorKeys(prev => {
    if (!prev.has(key)) return prev;
    const next = new Set(prev);
    next.delete(key);
    return next;
  });
  const [hasUsedAI, setHasUsedAI] = useState(false);
  // idle | expanding | reviewing | skipped | suggesting | elements | integrating | final_review | refine | refining | error
  const [aiFlowState, setAiFlowState] = useState('idle');
  const [aiExpandedUpdates, setAiExpandedUpdates] = useState(null);
  const [aiExpandedLabels, setAiExpandedLabels] = useState({});
  const [expandedEditorKey, setExpandedEditorKey] = useState(null);
  const [elementSuggestions, setElementSuggestions] = useState([]);
  const [selectedElements, setSelectedElements] = useState(new Set());
  const [photoSuggestions, setPhotoSuggestions] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [refineText, setRefineText] = useState('');
  const [hasStartedEditing, setHasStartedEditing] = useState(false);
  const aiFlowStartedRef = useRef(false);
  const photosOfferedRef = useRef(false);
  // Decline invitation
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await axios.post(`${API_URL}/api/contribute/${token}/decline`, { reason: declineReason });
      setDeclined(true);
      setDeclineDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No hemos podido registrar tu respuesta. Inténtalo de nuevo.');
    } finally {
      setDeclining(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, [token]);

  // Auto-start the AI enrichment flow when the contributor reaches the last
  // step. Runs once — the contributor was informed in the anecdotes section.
  useEffect(() => {
    if (!template || submitted || declined) return;
    const lastIdx = (template.sections?.length || 1) - 1;
    if (currentStep === lastIdx && !aiFlowStartedRef.current) {
      aiFlowStartedRef.current = true;
      expandAllWithAI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, template, submitted, declined]);

  const fetchInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contribute/${token}`);
      setInfo(res.data);
      if (res.data.has_contributed) {
        setSubmitted(true);
      }
      if (res.data.status === 'declined') {
        setDeclined(true);
      }
      const tmpl = res.data.form_template;
      setTemplate(tmpl);
      // Initialize formData with defaults
      const initial = {};
      if (tmpl?.sections) {
        tmpl.sections.forEach(section => {
          if (section.repeatable) {
            initial[section.id] = [{}];
          } else {
            initial[section.id] = {};
          }
        });
      }
      // Pre-fill contributor info
      if (initial.who) {
        initial.who.contributor_name = res.data.contributor_name || '';
        if (res.data.contributor_last_name) {
          initial.who.contributor_last_name = res.data.contributor_last_name;
        }
        initial.who.contributor_email = res.data.contributor_email || '';
        initial.who.contributor_phone = res.data.contributor_phone || '';
        if (res.data.contributor_relationship) {
          initial.who.relationship = res.data.contributor_relationship;
        }
      }
      setFormData(initial);
    } catch (err) {
      toast.error('Enlace no valido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const updateSectionData = (sectionId, data) => {
    setHasStartedEditing(true);
    setFormData(prev => ({ ...prev, [sectionId]: data }));
  };

  const updateFieldValue = (sectionId, fieldId, value) => {
    setHasStartedEditing(true);
    setFormData(prev => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], [fieldId]: value }
    }));
    clearError(`${sectionId}.${fieldId}`);
  };

  // Count total words across all textarea fields
  const getTotalWords = () => {
    let total = 0;
    if (!template?.sections) return 0;
    for (const section of template.sections) {
      const data = formData[section.id];
      if (!data) continue;
      for (const field of section.fields) {
        if (field.type !== 'textarea') continue;
        if (section.repeatable && Array.isArray(data)) {
          for (const inst of data) {
            const txt = inst?.[field.id] || '';
            total += txt.trim().split(/\s+/).filter(Boolean).length;
          }
        } else {
          const txt = data?.[field.id] || '';
          total += txt.trim().split(/\s+/).filter(Boolean).length;
        }
      }
    }
    return total;
  };

  const expandAllWithAI = async () => {
    // Step 1 — first ask the AI for follow-up questions tailored to the
    // contributor's text. They are optional but yield much richer expansions
    // because they pull facts the AI is not allowed to invent.
    setAiFlowState('expanding');
    try {
      const qRes = await axios.post(
        `${API_URL}/api/contribute/${token}/suggest-questions`,
        { form_data: formData }
      );
      const qs = qRes.data?.questions || [];
      if (qs.length === 0) {
        await runExpansion(formData);
      } else {
        setFollowupQuestions(qs);
        setFollowupAnswers({});
        setQuestionsState('offered');
        setAiFlowState('idle');
      }
    } catch {
      await runExpansion(formData);
    }
  };

  // Run the actual AI expansion with whatever form_data we have (with or
  // without follow-up answers attached). Shared by all entry points.
  const runExpansion = async (payload) => {
    setAiFlowState('expanding');
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/expand-all`, {
        form_data: payload,
      });
      setAiExpandedUpdates(res.data.updates);
      setAiExpandedLabels(res.data.labels || {});
      setAiFlowState('reviewing');
    } catch {
      toast.error('Error al expandir el contenido con IA');
      setAiFlowState('error');
    }
  };

  const applyUpdates = (updates) => {
    const newFormData = { ...formData };
    for (const [key, value] of Object.entries(updates)) {
      const [sectionId, fieldId, instIdx] = key.split('.');
      if (instIdx !== '' && instIdx !== undefined) {
        const arr = [...(newFormData[sectionId] || [])];
        arr[parseInt(instIdx)] = { ...(arr[parseInt(instIdx)] || {}), [fieldId]: value };
        newFormData[sectionId] = arr;
      } else {
        newFormData[sectionId] = { ...(newFormData[sectionId] || {}), [fieldId]: value };
      }
    }
    setFormData(newFormData);
    return newFormData;
  };

  // Draft accepted → ask the AI for concrete elements worth deepening.
  const acceptAllExpanded = async () => {
    if (!aiExpandedUpdates) return;
    applyUpdates(aiExpandedUpdates);
    setHasUsedAI(true);
    setAiFlowState('suggesting');
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/suggest-elements`, { updates: aiExpandedUpdates, who: formData.who });
      const els = res.data?.elements || [];
      if (els.length === 0) {
        fetchPhotoSuggestions();
        return;
      }
      setElementSuggestions(els);
      setSelectedElements(new Set(els.map((_, i) => i)));
      setAiFlowState('elements');
    } catch {
      setAiFlowState('refine');
    }
  };

  const toggleElement = (idx) => {
    setSelectedElements(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const integrateSelectedElements = async () => {
    const chosen = elementSuggestions.filter((_, i) => selectedElements.has(i));
    if (chosen.length === 0) {
      setAiFlowState('refine');
      return;
    }
    setAiFlowState('integrating');
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/integrate-elements`, {
        updates: aiExpandedUpdates, elements: chosen, who: formData.who,
      });
      setAiExpandedUpdates(res.data.updates);
      setAiFlowState('final_review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al integrar la información');
      setAiFlowState('refine');
    }
  };

  const acceptFinalReview = () => {
    if (aiExpandedUpdates) applyUpdates(aiExpandedUpdates);
    fetchPhotoSuggestions();
  };

  // Offer AI-generated illustrative photos once per contribution.
  const fetchPhotoSuggestions = async () => {
    if (photosOfferedRef.current) {
      setAiFlowState('refine');
      return;
    }
    photosOfferedRef.current = true;
    setAiFlowState('photos_loading');
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/suggest-photos`, { updates: aiExpandedUpdates });
      const ph = res.data?.photos || [];
      if (ph.length === 0) {
        setAiFlowState('refine');
        return;
      }
      setPhotoSuggestions(ph);
      setSelectedPhotos(new Set(ph.map((_, i) => i)));
      setAiFlowState('photos');
    } catch {
      setAiFlowState('refine');
    }
  };

  const togglePhoto = (idx) => {
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const addSelectedPhotos = () => {
    const chosen = photoSuggestions.filter((_, i) => selectedPhotos.has(i));
    if (chosen.length === 0) {
      setAiFlowState('refine');
      return;
    }
    setFormData(prev => {
      const arr = [...(prev.anecdote || [])];
      chosen.forEach(ph => {
        const parts = (ph.key || '').split('.');
        let idx = parseInt(parts[2]);
        if (isNaN(idx) || idx < 0 || idx >= arr.length) idx = 0;
        const inst = { ...(arr[idx] || {}) };
        const photos = [...(inst.photos || [])];
        if (photos.length < 3) {
          photos.push({ photo_id: ph.photo_id, url: `${API_URL}${ph.url}`, storage_path: ph.storage_path });
        }
        inst.photos = photos;
        arr[idx] = inst;
      });
      return { ...prev, anecdote: arr };
    });
    toast.success('Fotos añadidas a tu anécdota');
    setAiFlowState('refine');
  };

  const runRefine = async () => {
    if (!refineText.trim()) return;
    setAiFlowState('refining');
    try {
      const res = await axios.post(`${API_URL}/api/contribute/${token}/integrate-elements`, {
        updates: aiExpandedUpdates, instruction: refineText.trim(), who: formData.who,
      });
      setAiExpandedUpdates(res.data.updates);
      setRefineText('');
      setAiFlowState('final_review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ampliar el contenido');
      setAiFlowState('refine');
    }
  };

  const declineAI = () => {
    setAiFlowState('skipped');
    setAiExpandedUpdates(null);
  };

  const buildPdfSections = () => {
    if (!aiExpandedUpdates || !aiExpandedLabels) return [];
    return Object.entries(aiExpandedUpdates).map(([key, text]) => ({
      label: aiExpandedLabels[key] || key.split('.')[1],
      text
    }));
  };

  const handleContributionPdf = async (mode) => {
    const sections = buildPdfSections();
    if (!sections.length) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/contribute/${token}/preview-contribution-pdf`,
        {
          sections,
          book_title: info?.book_title || 'Libro',
          contributor_name: info?.contributor_name || 'Colaborador',
          mode
        },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      if (mode === 'inline') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Aportacion_${(info?.contributor_name || 'Colaborador').replace(/\s/g, '_')}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('Error al generar el PDF');
    }
  };

  const validateStep = () => {
    if (!template) return true;
    const section = template.sections[currentStep];
    if (!section) return true;

    const data = formData[section.id];
    const missing = [];
    for (const field of section.fields) {
      if (!field.required) continue;
      if (section.repeatable) {
        const instances = Array.isArray(data) ? data : [data];
        instances.forEach((inst, idx) => {
          const v = inst?.[field.id];
          if (!v || (typeof v === 'string' && !v.trim())) {
            missing.push({ key: `${section.id}.${idx}.${field.id}`, label: `${field.label}${instances.length > 1 ? ` (#${idx + 1})` : ''}` });
          }
        });
      } else {
        const val = data?.[field.id];
        if (field.id === 'three_words') {
          const words = (val || '').split('|||').filter(w => w.trim());
          if (words.length === 0) missing.push({ key: `${section.id}.${field.id}`, label: field.label });
        } else if (!val || (typeof val === 'string' && !val.trim())) {
          missing.push({ key: `${section.id}.${field.id}`, label: field.label });
        }
      }
    }
    if (missing.length > 0) {
      setErrorKeys(new Set(missing.map(m => m.key)));
      const summary = missing.length === 1
        ? `Falta por rellenar: "${missing[0].label}"`
        : `Faltan ${missing.length} campos obligatorios. Los hemos marcado en rojo.`;
      toast.error(summary);
      return false;
    }
    setErrorKeys(new Set());
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setHasStartedEditing(true);
      setCurrentStep(prev => Math.min(prev + 1, (template?.sections?.length || 1) - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Follow-up questions before AI expansion (option B v2):
  // when the contributor accepts AI improvement, we first ask them 3
  // tailored questions to enrich the expansion with confirmed facts.
  const [questionsState, setQuestionsState] = useState('idle'); // idle | offered | answering | submitting
  const [followupQuestions, setFollowupQuestions] = useState([]);
  const [followupAnswers, setFollowupAnswers] = useState({});

  const skipFollowupAndExpand = async () => {
    setQuestionsState('idle');
    await runExpansion(formData);
  };

  const expandWithAnswers = async () => {
    const qa = followupQuestions
      .map((q, i) => ({ question: q, answer: (followupAnswers[i] || '').trim() }))
      .filter((qa) => qa.answer);
    setQuestionsState('idle');
    await runExpansion({ ...formData, _followup_qa: qa });
  };

  const doSubmit = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contribute/${token}`, payload);
      setSubmitted(true);
      toast.success('Contribucion enviada correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    const payload = aiExpandedUpdates ? applyUpdates(aiExpandedUpdates) : formData;
    await doSubmit(payload);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  if (!info || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center space-y-3">
            <p className="text-lg font-medium text-red-600">Enlace no valido</p>
            <p className="text-sm text-muted-foreground">Este enlace de contribucion no existe o ha expirado.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return <ContributorThankYou info={info} token={token} />;
  }

  if (declined) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card data-testid="declined-screen">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-heading font-bold">Gracias por avisarnos</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Hemos registrado que prefieres no participar en el libro de <strong>{info?.recipient_name}</strong>.
                No volveremos a contactarte sobre este libro. Si cambias de opinión, ponte en contacto con
                el promotor del libro y le pedirá una nueva invitación.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const sections = template.sections;
  const currentSection = sections[currentStep];
  const isLastStep = currentStep === sections.length - 1;
  const progress = ((currentStep + 1) / sections.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-2xl mx-auto px-4 py-4">
          {/* Language switcher — contributors come from any region; let
              them switch the form to a language they understand. */}
          <div className="flex justify-end mb-2">
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="text-[11px] border rounded px-1.5 py-0.5 bg-muted/30 cursor-pointer"
              data-testid="contribute-language-switcher"
              aria-label="Cambiar idioma"
            >
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            {info.protagonist_photo_id ? (
              <img
                src={`${API_URL}/api/anecdote-photo/${info.protagonist_photo_id}`}
                alt={info.recipient_name}
                className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-primary/20 shadow-sm"
                data-testid="protagonist-header-photo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-heading font-bold text-lg">{info.book_title}</h1>
              <p className="text-sm text-muted-foreground">Contribuye al libro de {info.recipient_name}</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed" data-testid="form-intro">
            Estamos creando un libro personalizado para <strong>{info.recipient_name}</strong> con las historias y recuerdos de las personas mas cercanas. Tus anecdotas y vivencias seran la base para escribir un libro unico e irrepetible. Cuanto mas detalle compartas, mas especial sera el resultado.
          </p>
          {info.recommended_by_name && (
            <div className="mt-3 p-3 rounded-md bg-orange-50 border border-orange-200 text-sm" data-testid="recommended-by-banner">
              <span className="text-orange-800">
                ✨ Has sido recomendado/a por <strong>{info.recommended_by_name}</strong> para participar en este libro.
              </span>
            </div>
          )}
          {!hasStartedEditing && (
            <div className="mt-3 text-right">
              <button
                type="button"
                onClick={() => setDeclineDialogOpen(true)}
                className="text-xs text-muted-foreground hover:text-red-600 underline underline-offset-2"
                data-testid="decline-invitation-link"
              >
                ¿Prefieres no participar? Declinar invitación
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground">Paso {currentStep + 1} de {sections.length}</span>
          <span className="text-xs font-medium">{currentSection.title}</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card className="card-elevated" data-testid={`form-step-${currentStep}`}>
          <CardHeader>
            <CardTitle className="font-heading text-xl">{currentSection.title}</CardTitle>
            {currentSection.description && (
              <CardDescription>{currentSection.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {currentSection.repeatable ? (
              <RepeatableSection
                section={currentSection}
                instances={Array.isArray(formData[currentSection.id]) ? formData[currentSection.id] : [formData[currentSection.id] || {}]}
                onUpdate={(data) => updateSectionData(currentSection.id, data)}
                lang={info?.language || 'es'}
                errorKeys={errorKeys}
                clearError={clearError}
                inviteToken={token}
              />
            ) : (
              <div className="space-y-5">
                {currentSection.fields.map((field) => {
                  const ctx = formData[currentSection.id] || {};
                  if (field.show_if && ctx[field.show_if.field] !== field.show_if.equals) return null;
                  // Info fields skip the label/required asterisk wrapper.
                  if (field.type === 'info') {
                    return (
                      <DynamicField key={field.id}
                        field={field} value={null} onChange={() => {}}
                        lang={info?.language || 'es'} />
                    );
                  }
                  const fieldKey = `${currentSection.id}.${field.id}`;
                  const isMissing = errorKeys.has(fieldKey);
                  return (
                  <div key={field.id}
                    className={isMissing ? 'rounded-md ring-2 ring-red-400 bg-red-50/40 p-2 -mx-1' : ''}>
                    {field.id === 'three_words' ? (
                      <>
                        <Label className="input-label text-sm mb-1.5 block">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                          <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (mínimo 1, puedes añadir hasta 3)
                          </span>
                        </Label>
                        <div className="grid grid-cols-3 gap-3" data-testid="three-words-inputs">
                          {[0, 1, 2].map((i) => {
                            const words = (formData[currentSection.id]?.three_words || '').split('|||');
                            return (
                              <Input
                                key={i}
                                value={words[i] || ''}
                                onChange={(e) => {
                                  const current = (formData[currentSection.id]?.three_words || '').split('|||');
                                  current[i] = e.target.value;
                                  updateFieldValue(currentSection.id, 'three_words', current.join('|||'));
                                }}
                                placeholder={i === 0 ? 'Palabra 1 (obligatoria)' : `Palabra ${i + 1} (opcional)`}
                                className="input-field"
                                data-testid={`three-words-${i}`}
                              />
                            );
                          })}
                        </div>
                      </>
                    ) : field.type === 'photos' ? (
                      <>
                        <Label className="input-label text-sm mb-1.5 block">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <AnecdotePhotoUpload
                          photos={formData[currentSection.id]?.[field.id] || []}
                          onChange={(photos) => updateFieldValue(currentSection.id, field.id, photos)}
                          bookId={info?.book_id || ''}
                          context="contributor"
                          inviteToken={token}
                          maxPhotos={field.max_photos || 1}
                        />
                        {field.placeholder && (
                          <p className="text-xs text-muted-foreground mt-1">{field.placeholder}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <Label className="input-label text-sm mb-1.5 block">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <DynamicField
                          field={field}
                          value={formData[currentSection.id]?.[field.id]}
                          onChange={(val) => updateFieldValue(currentSection.id, field.id, val)}
                          lang={info?.language || 'es'}
                        />
                      </>
                    )}
                    {isMissing && (
                      <p className="text-[11px] text-red-600 mt-1">Este campo es obligatorio.</p>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Total Word Count & AI Flow - shown on last step */}
        {isLastStep && (() => {
          const totalWords = getTotalWords();
          const pct = Math.min(100, (totalWords / MIN_WORDS) * 100);
          const isOk = totalWords >= MIN_WORDS;
          return (
            <Card className={`mt-4 ${isOk ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`} data-testid="total-word-count">
              <CardContent className="py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Total de palabras: <strong className={isOk ? 'text-green-700' : 'text-amber-700'}>{totalWords}</strong> / {MIN_WORDS}
                  </p>
                  <span className={`text-xs font-medium ${isOk ? 'text-green-600' : 'text-amber-600'}`}>{Math.round(pct)}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isOk ? 'bg-green-500' : pct > 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>

                {/* State: Content is sufficient */}
                {isOk && (
                  <p className="text-xs text-green-700 font-medium" data-testid="word-count-ok">
                    Tu aportacion tiene suficiente contenido. Puedes enviarla.
                  </p>
                )}

                {/* State: waiting for the AI auto-start */}
                {aiFlowState === 'idle' && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3" data-testid="ai-auto-info">
                    <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-purple-700">
                      La IA va a redactar una version enriquecida de tus anecdotas para que la revises antes de enviarla.
                    </p>
                  </div>
                )}

                {/* State: AI is expanding */}
                {aiFlowState === 'expanding' && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3" data-testid="ai-expanding">
                    <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-purple-800">Generando contenido ampliado...</p>
                      <p className="text-xs text-purple-600">La IA esta enriqueciendo tus anecdotas. Esto puede tardar unos segundos.</p>
                    </div>
                  </div>
                )}

                {/* State: AI result ready for review (draft or after integration) */}
                {(aiFlowState === 'reviewing' || aiFlowState === 'final_review') && aiExpandedUpdates && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid="ai-review">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-purple-800 font-medium">
                          {aiFlowState === 'reviewing' ? 'Contenido redactado listo' : 'Texto completo de tu aportacion'}
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                          {aiFlowState === 'reviewing'
                            ? 'Hemos redactado una version enriquecida de tus anecdotas. Puedes editar cada campo antes de aceptar.'
                            : 'Este es el texto completo tras integrar la informacion. Revisalo con calma; si necesitas cambiar algo, usa el boton de editar.'}
                        </p>
                      </div>
                    </div>
                    <div className="ml-7 space-y-3">
                      {Object.entries(aiExpandedUpdates).map(([key, text]) => {
                        const label = aiExpandedLabels[key] || key.split('.')[1];
                        const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
                        const instIdx = parseInt(key.split('.')[2]);
                        const anecdoteTitle = !isNaN(instIdx) ? (formData.anecdote?.[instIdx]?.title || `Anecdota ${instIdx + 1}`) : null;
                        return (
                          <div key={key} className="bg-white border rounded-md p-3">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-purple-700">
                                {aiFlowState === 'final_review' && anecdoteTitle ? `${anecdoteTitle} — ${label}` : label}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{wordCount} palabras</span>
                                <button
                                  type="button"
                                  onClick={() => setExpandedEditorKey(key)}
                                  className="p-1 rounded hover:bg-purple-100 text-purple-500 hover:text-purple-700 transition-colors"
                                  title={aiFlowState === 'final_review' ? 'Editar texto' : 'Ampliar editor'}
                                  data-testid={`expand-editor-${key}`}
                                >
                                  {aiFlowState === 'final_review' ? <Pencil className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </div>
                            {aiFlowState === 'final_review' ? (
                              <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid={`ai-final-text-${key}`}>{text}</p>
                            ) : (
                              <Textarea
                                value={text}
                                onChange={(e) => setAiExpandedUpdates(prev => ({ ...prev, [key]: e.target.value }))}
                                className="min-h-[120px] text-sm"
                                data-testid={`ai-review-field-${key}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Expanded Editor Dialog */}
                    <Dialog open={!!expandedEditorKey} onOpenChange={(open) => { if (!open) setExpandedEditorKey(null); }}>
                      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="expanded-editor-dialog">
                        <DialogHeader>
                          <DialogTitle className="text-purple-800 flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            {expandedEditorKey && (aiExpandedLabels[expandedEditorKey] || expandedEditorKey?.split('.')[1])}
                          </DialogTitle>
                          <p className="text-xs text-muted-foreground">Edita el contenido con mas espacio y comodidad</p>
                        </DialogHeader>
                        <div className="flex-1 min-h-0 py-2">
                          <Textarea
                            value={expandedEditorKey ? (aiExpandedUpdates?.[expandedEditorKey] || '') : ''}
                            onChange={(e) => {
                              const k = expandedEditorKey;
                              if (k) setAiExpandedUpdates(prev => ({ ...prev, [k]: e.target.value }));
                            }}
                            className="min-h-[50vh] text-sm leading-relaxed"
                            data-testid="expanded-editor-textarea"
                          />
                          <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                            <span>
                              {expandedEditorKey && (aiExpandedUpdates?.[expandedEditorKey] || '').trim().split(/\s+/).filter(Boolean).length} palabras
                            </span>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button size="sm" onClick={() => setExpandedEditorKey(null)} data-testid="close-expanded-editor-btn">
                            Cerrar
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <div className="flex flex-wrap gap-2 ml-7">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => handleContributionPdf('attachment')}
                        data-testid="download-contribution-pdf-btn"
                      >
                        <Download className="w-3 h-3" /> Descargar PDF
                      </Button>
                    </div>
                    <div className="flex gap-2 ml-7">
                      {aiFlowState === 'reviewing' ? (
                        <>
                          <Button
                            size="sm"
                            className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={acceptAllExpanded}
                            data-testid="accept-ai-all-btn"
                          >
                            <CheckCircle className="w-3 h-3" /> Aceptar y continuar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={declineAI}
                            data-testid="reject-ai-all-btn"
                          >
                            Mantener mis textos originales
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={acceptFinalReview}
                          data-testid="accept-final-review-btn"
                        >
                          <CheckCircle className="w-3 h-3" /> Aceptar y continuar
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* State: analysing for deepening suggestions */}
                {aiFlowState === 'suggesting' && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3" data-testid="ai-suggesting">
                    <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-purple-800">Analizando tu aportacion...</p>
                      <p className="text-xs text-purple-600">Buscamos elementos de tu historia sobre los que podriamos anadir informacion interesante.</p>
                    </div>
                  </div>
                )}

                {/* State: element suggestions to deepen */}
                {aiFlowState === 'elements' && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid="ai-elements">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-purple-800 font-medium">¿Quieres que profundicemos en algun elemento?</p>
                        <p className="text-xs text-purple-600 mt-1">
                          Hemos detectado estos elementos en tu aportacion. Marca los que quieras y anadiremos una breve nota informativa (maximo 100 palabras) integrada en el texto.
                        </p>
                      </div>
                    </div>
                    <div className="ml-7 space-y-2">
                      {elementSuggestions.map((el, i) => (
                        <label key={i} className="flex items-start gap-2.5 bg-white border rounded-md p-2.5 cursor-pointer hover:bg-purple-50/50" data-testid={`ai-element-${i}`}>
                          <input
                            type="checkbox"
                            checked={selectedElements.has(i)}
                            onChange={() => toggleElement(i)}
                            className="mt-0.5 accent-purple-600"
                            data-testid={`ai-element-checkbox-${i}`}
                          />
                          <span className="text-sm">
                            <strong className="text-purple-800">{el.term}</strong>
                            {el.reason && <span className="text-muted-foreground"> — {el.reason}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 ml-7">
                      <Button
                        size="sm"
                        className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={integrateSelectedElements}
                        disabled={selectedElements.size === 0}
                        data-testid="integrate-elements-btn"
                      >
                        <Sparkles className="w-3 h-3" /> Anadir informacion seleccionada
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchPhotoSuggestions}
                        data-testid="skip-elements-btn"
                      >
                        No anadir nada
                      </Button>
                    </div>
                  </div>
                )}

                {/* State: integrating info / applying a refine request */}
                {(aiFlowState === 'integrating' || aiFlowState === 'refining') && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3" data-testid="ai-integrating">
                    <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-purple-800">Integrando la informacion en tu texto...</p>
                      <p className="text-xs text-purple-600">Esto puede tardar unos segundos.</p>
                    </div>
                  </div>
                )}

                {/* State: generating photo suggestions */}
                {aiFlowState === 'photos_loading' && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3" data-testid="ai-photos-loading">
                    <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-purple-800">Buscando fotos relacionadas con tu historia...</p>
                      <p className="text-xs text-purple-600">Generamos algunas imagenes ilustrativas a partir de tu relato. Esto puede tardar hasta un minuto.</p>
                    </div>
                  </div>
                )}

                {/* State: photo suggestions */}
                {aiFlowState === 'photos' && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid="ai-photos">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-purple-800 font-medium">¿Quieres anadir alguna de estas fotos?</p>
                        <p className="text-xs text-purple-600 mt-1">
                          Son imagenes ilustrativas generadas a partir de tu relato. Marca las que quieras y las anadiremos a tu anecdota.
                        </p>
                      </div>
                    </div>
                    <div className="ml-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {photoSuggestions.map((ph, i) => (
                        <label
                          key={i}
                          className={`relative block border-2 rounded-lg overflow-hidden cursor-pointer transition-colors ${selectedPhotos.has(i) ? 'border-purple-500' : 'border-stone-200'}`}
                          data-testid={`ai-photo-${i}`}
                        >
                          <input
                            type="checkbox"
                            className="absolute top-2 left-2 z-10 w-4 h-4 accent-purple-600"
                            checked={selectedPhotos.has(i)}
                            onChange={() => togglePhoto(i)}
                            data-testid={`ai-photo-checkbox-${i}`}
                          />
                          <img src={`${API_URL}${ph.url}`} alt={ph.caption || 'Foto sugerida'} className="w-full h-32 object-cover" />
                          {ph.caption && <p className="text-[11px] text-muted-foreground p-1.5 bg-white leading-snug">{ph.caption}</p>}
                        </label>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 ml-7">
                      <Button
                        size="sm"
                        className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={addSelectedPhotos}
                        disabled={selectedPhotos.size === 0}
                        data-testid="add-photos-btn"
                      >
                        <Camera className="w-3 h-3" /> Anadir seleccionadas
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAiFlowState('refine')}
                        data-testid="skip-photos-btn"
                      >
                        No anadir fotos
                      </Button>
                    </div>
                  </div>
                )}

                {/* State: offer a final targeted expansion, or finish */}
                {aiFlowState === 'refine' && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid="ai-refine">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-purple-800 font-medium">¿Quieres que ampliemos algo en concreto?</p>
                        <p className="text-xs text-purple-600 mt-1">
                          Dinos sobre que parte del texto quieres mas informacion. Si no, puedes finalizar y enviar tu aportacion.
                        </p>
                      </div>
                    </div>
                    <div className="ml-7 space-y-2">
                      <Textarea
                        value={refineText}
                        onChange={(e) => setRefineText(e.target.value)}
                        placeholder='Ej.: "Amplia la parte del huerto" o "anade mas contexto sobre los anos 80"'
                        className="min-h-[70px] text-sm bg-white"
                        data-testid="refine-input"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 ml-7">
                      <Button
                        size="sm"
                        className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={runRefine}
                        disabled={!refineText.trim()}
                        data-testid="refine-btn"
                      >
                        <Sparkles className="w-3 h-3" /> Ampliar con IA
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={handleSubmit}
                        disabled={submitting}
                        data-testid="finish-and-submit-btn"
                      >
                        <Send className="w-3 h-3" /> {submitting ? 'Enviando...' : 'No, finalizar y enviar mi aportacion'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => handleContributionPdf('attachment')}
                        data-testid="refine-download-pdf-btn"
                      >
                        <Download className="w-3 h-3" /> Descargar PDF
                      </Button>
                    </div>
                  </div>
                )}

                {/* State: contributor kept their original texts */}
                {aiFlowState === 'skipped' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="ai-skipped">
                    <p className="text-sm text-blue-800">
                      De acuerdo, enviaremos tus textos <strong>tal y como los escribiste</strong>. Pulsa "Enviar aportacion" cuando estes listo.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={expandAllWithAI}
                      data-testid="reconsider-ai-btn"
                    >
                      <Sparkles className="w-3 h-3" /> He cambiado de idea, redactar con IA
                    </Button>
                  </div>
                )}

                {/* State: AI failed */}
                {aiFlowState === 'error' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="ai-error">
                    <p className="text-sm text-amber-800">
                      No hemos podido generar la version enriquecida en este momento. Puedes reintentarlo o enviar tu aportacion tal como esta.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={expandAllWithAI}
                      data-testid="retry-ai-btn"
                    >
                      <Sparkles className="w-3 h-3" /> Reintentar con IA
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="gap-2"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </Button>

          {isLastStep ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || ['expanding', 'suggesting', 'integrating', 'refining', 'reviewing', 'elements', 'final_review', 'photos_loading', 'photos'].includes(aiFlowState)}
              className="gap-2"
              data-testid="submit-contribution-btn"
            >
              {submitting ? 'Enviando...' : <><Send className="w-4 h-4" /> Enviar aportacion</>}
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="gap-2"
              data-testid="next-step-btn"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Follow-up Questions Dialog — appears AFTER the contributor accepts
          AI improvement and BEFORE the AI generates the expanded version.
          Their answers are sent as confirmed facts, so the expansion is
          richer without inventing anything. */}
      <Dialog
        open={questionsState === 'offered' || questionsState === 'answering'}
        onOpenChange={(open) => {
          if (!open && questionsState === 'offered') skipFollowupAndExpand();
        }}
      >
        <DialogContent className="sm:max-w-[560px]" data-testid="followup-dialog">
          {questionsState === 'offered' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Hemos pensado en {followupQuestions.length} preguntas
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm text-muted-foreground">
                  Antes de que la IA mejore tu texto, nos gustaria pedirte detalles concretos
                  que <strong>no se pueden inventar</strong>. Responder ayudara a que tu aportacion
                  sea mas autentica y emotiva.
                </p>
                <ul className="space-y-2">
                  {followupQuestions.map((q, i) => (
                    <li key={i} className="text-sm flex gap-2 items-start" data-testid={`followup-q-preview-${i}`}>
                      <span className="text-primary font-bold flex-shrink-0">·</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
                <Button
                  variant="outline"
                  onClick={skipFollowupAndExpand}
                  data-testid="followup-skip-btn"
                >
                  Mejorar sin responder
                </Button>
                <Button
                  onClick={() => setQuestionsState('answering')}
                  className="gap-2"
                  data-testid="followup-accept-btn"
                >
                  <Sparkles className="w-4 h-4" /> Si, responderlas
                </Button>
              </DialogFooter>
            </>
          )}

          {questionsState === 'answering' && (
            <>
              <DialogHeader>
                <DialogTitle>Responde con lo primero que recuerdes</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
                <p className="text-xs text-muted-foreground">
                  Tras enviarlas, la IA generara una version mejorada con tus detalles.
                  Puedes saltar las que no recuerdes.
                </p>
                {followupQuestions.map((q, i) => (
                  <div key={i} className="space-y-1.5">
                    <label className="text-sm font-medium leading-snug block">
                      {i + 1}. {q}
                    </label>
                    <Textarea
                      rows={3}
                      value={followupAnswers[i] || ''}
                      onChange={(e) =>
                        setFollowupAnswers((prev) => ({ ...prev, [i]: e.target.value }))
                      }
                      placeholder="Tu respuesta..."
                      data-testid={`followup-answer-${i}`}
                    />
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
                <Button
                  variant="outline"
                  onClick={skipFollowupAndExpand}
                  data-testid="followup-skip-from-answers-btn"
                >
                  Mejorar sin estas respuestas
                </Button>
                <Button
                  onClick={expandWithAnswers}
                  className="gap-2"
                  data-testid="followup-submit-btn"
                >
                  <Sparkles className="w-4 h-4" /> Mejorar con IA
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decline Invitation Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent className="max-w-md" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle>¿Seguro que prefieres no participar?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Si declinas la invitación, le avisaremos al promotor del libro para que no insista. No volverás a recibir
              recordatorios sobre el libro de <strong>{info?.recipient_name}</strong>. Esta acción no se puede deshacer
              desde aquí, aunque siempre puedes pedirle al promotor una nueva invitación.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">¿Quieres dejar un mensaje al promotor? (opcional)</Label>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Por ejemplo: 'Lo siento, ahora mismo no tengo tiempo'"
                className="mt-1 input-field min-h-[70px]"
                data-testid="decline-reason"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={() => setDeclineDialogOpen(false)} data-testid="cancel-decline-btn">
              Volver al formulario
            </Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={declining}
              className="gap-2"
              data-testid="confirm-decline-btn"
            >
              {declining ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Sí, declinar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContributePage;
